/**
 * /network — Influencer Network Directory
 * Full-width layout · Filter sidebar · Suggestions panel · Creator grid
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Trophy, Users, Star, Loader2, ChevronRight, Network, Zap,
  SlidersHorizontal, X, ChevronDown, Sparkles, TrendingUp, Clock,
  Filter, UserPlus, Globe, Youtube, Instagram, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import { leftTopArrowGradient, rightBottomArrowGradient } from '@/assets/svgs/gradientIcons';

/* ─── constants ──────────────────────────────────────────── */
const ROLES = [
  { id: '', label: 'All Roles' },
  { id: 'influencer', label: 'Influencer' },
  { id: 'creator', label: 'Creator' },
  { id: 'storyteller', label: 'Storyteller' },
  { id: 'ai_moviemaker', label: 'AI Moviemaker' },
  { id: 'ad_maker', label: 'Ad Maker' },
  { id: 'podcaster', label: 'Podcaster' },
  { id: 'video_editor', label: 'Video Editor' },
];

const LEVELS = [
  { id: '', label: 'All Levels' },
  { id: '1', label: 'Lv 1 · Newcomer' },
  { id: '2', label: 'Lv 2 · Maker' },
  { id: '3', label: 'Lv 3 · Creator' },
  { id: '4', label: 'Lv 4 · Featured' },
  { id: '5', label: 'Lv 5 · Influencer' },
  { id: '6', label: 'Lv 6 · Studio Lead' },
];

const SORTS = [
  { id: 'xp', label: 'Most XP', Icon: Zap },
  { id: 'recent', label: 'Recently Joined', Icon: Clock },
  { id: 'alpha', label: 'A → Z', Icon: TrendingUp },
];

const BADGES = ['joined', 'first_pin', 'showcase', 'portfolio', 'storyteller', 'producer', 'veteran'];
const BADGE_LABELS = {
  joined: '🌐 Joined', first_pin: '📌 Pin', showcase: '🎨 Showcase',
  portfolio: '🔗 Portfolio', storyteller: '📖 Story', producer: '🎬 Producer',
  veteran: '⭐ Veteran',
};

const LV_COL = ['#94a3b8','#7dd3fc','#c4b5fd','#67e8f9','#f0abfc','#62FAE3'];
const LV_LABELS = ['Newcomer','Maker','Creator','Featured','Influencer','Studio Lead'];

/* ─── helpers ────────────────────────────────────────────── */
const lvCol  = lv => LV_COL[Math.min((lv || 1) - 1, 5)];
const lvLabel = lv => LV_LABELS[Math.min((lv || 1) - 1, 5)];

function applyClientFilters(profiles, { role, level, badge, sort, q }) {
  let out = [...profiles];
  if (role)  out = out.filter(p => p.role === role);
  if (level) out = out.filter(p => String(p.progress?.level) === level);
  if (badge) out = out.filter(p => (p.progress?.badges || []).some(b => b.id === badge));
  if (q)     { const lq = q.toLowerCase(); out = out.filter(p =>
    (p.display_name||'').toLowerCase().includes(lq) ||
    (p.handle||'').toLowerCase().includes(lq) ||
    (p.bio||'').toLowerCase().includes(lq)); }
  if (sort === 'xp')     out.sort((a,b) => (b.progress?.xp||0) - (a.progress?.xp||0));
  if (sort === 'alpha')  out.sort((a,b) => (a.display_name||'').localeCompare(b.display_name||''));
  if (sort === 'recent') out.reverse();
  return out;
}

/* ─── Creator card ───────────────────────────────────────── */
function CreatorCard({ profile, onClick }) {
  const prog = profile.progress || {};
  const lv   = prog.level || 1;
  const col  = lvCol(lv);
  const init = (profile.display_name || profile.handle || '?')[0]?.toUpperCase();
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, transition: { duration: 0.15 } }}
      onClick={onClick}
      className="group cursor-pointer relative flex flex-col rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 transition-all duration-200 overflow-hidden"
      style={{ '--lv-col': col }}
    >
      {/* Top colour strip */}
      <div className="h-14 w-full relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${col}18 0%, transparent 70%)` }}>
        <div className="absolute inset-0 opacity-20"
          style={{ background: `radial-gradient(ellipse at 20% 50%, ${col} 0%, transparent 70%)` }} />
      </div>

      {/* Avatar pulled up */}
      <div className="px-4 -mt-6 flex items-end gap-3">
        <div className="w-12 h-12 rounded-xl border-2 border-[#0b0e13] flex items-center justify-center font-black text-base overflow-hidden flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${col}cc, ${col}55)`, color: '#080d14' }}>
          {profile.avatar_url
            ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
            : init}
        </div>
        <div className="mb-1 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border"
          style={{ color: col, borderColor: `${col}40`, background: `${col}12` }}>
          <Star className="w-2.5 h-2.5" /> Lv {lv} · {lvLabel(lv)}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 pt-2 flex flex-col gap-2 flex-1">
        <div>
          <p className="font-bold text-white text-sm leading-tight">{profile.display_name}</p>
          <p className="text-[11px] text-[#8a94a6]">@{profile.handle}</p>
        </div>
        {profile.role && (
          <span className="self-start text-[10px] px-2 py-0.5 rounded-full bg-[#D0BCFF]/10 text-[#D0BCFF] border border-[#D0BCFF]/20">
            {ROLES.find(r => r.id === profile.role)?.label || profile.role}
          </span>
        )}
        {profile.bio && (
          <p className="text-[11px] text-[#8a94a6] line-clamp-2 leading-relaxed">{profile.bio}</p>
        )}
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-white/[0.07]">
          <span className="text-[11px] text-[#8a94a6] flex items-center gap-1">
            <Zap className="w-3 h-3" style={{ color: col }} />
            {(prog.xp || 0).toLocaleString()} XP
          </span>
          {(prog.badges || []).length > 0 && (
            <span className="text-[10px] text-[#8a94a6]">{prog.badges.length} badge{prog.badges.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Suggestion card ────────────────────────────────────── */
function SuggestCard({ profile, onClick }) {
  const prog = profile.progress || {};
  const lv = prog.level || 1;
  const col = lvCol(lv);
  return (
    <div onClick={onClick}
      className="group cursor-pointer flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.05] transition-all border border-transparent hover:border-white/[0.08]">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${col}cc, ${col}44)`, color: '#080d14' }}>
        {profile.avatar_url
          ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
          : (profile.display_name || '?')[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{profile.display_name}</p>
        <p className="text-[10px] text-[#8a94a6]">@{profile.handle} · Lv {lv}</p>
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-[#8a94a6] group-hover:text-[#62FAE3] flex-shrink-0 transition-colors" />
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────── */
export const NetworkDirectoryPage = () => {
  const navigate = useNavigate();
  const [allProfiles, setAllProfiles]     = useState([]);
  const [displayed, setDisplayed]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [q, setQ]                         = useState('');
  const [roleFilter, setRoleFilter]       = useState('');
  const [levelFilter, setLevelFilter]     = useState('');
  const [badgeFilter, setBadgeFilter]     = useState('');
  const [sortBy, setSortBy]               = useState('xp');
  const [filtersOpen, setFiltersOpen]     = useState(false);
  const [myProfile, setMyProfile]         = useState(null);
  const searchRef                         = useRef(null);

  /* Fetch all profiles once */
  const loadAll = useCallback(async () => {
    try {
      const [dirRes, meRes] = await Promise.all([
        api.get('/network?limit=200'),
        api.get('/network/me').catch(() => ({ data: {} })),
      ]);
      const profiles = dirRes.data.profiles || [];
      setAllProfiles(profiles);
      setMyProfile(meRes.data?.profile || null);
    } catch { toast.error('Failed to load network'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* Refilter whenever deps change */
  useEffect(() => {
    setDisplayed(applyClientFilters(allProfiles, { role: roleFilter, level: levelFilter, badge: badgeFilter, sort: sortBy, q }));
  }, [allProfiles, q, roleFilter, levelFilter, badgeFilter, sortBy]);

  /* Suggestions: top XP profiles not yet connected (simple heuristic: first 5 by XP) */
  const suggestions = allProfiles
    .filter(p => p.handle !== myProfile?.handle)
    .sort((a, b) => (b.progress?.xp || 0) - (a.progress?.xp || 0))
    .slice(0, 6);

  const activeFilterCount = [roleFilter, levelFilter, badgeFilter].filter(Boolean).length;

  const clearFilters = () => { setRoleFilter(''); setLevelFilter(''); setBadgeFilter(''); setQ(''); };

  return (
    <div className="min-h-screen bg-transparent text-white relative overflow-x-hidden">
      {/* Ambient glows */}
      <div className="fixed top-0 left-0 w-[800px] pointer-events-none z-0 opacity-40"
        style={{ transform: 'translate(-25%,-25%)' }}
        dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }} />
      <div className="fixed bottom-0 right-0 w-[700px] pointer-events-none z-0 opacity-40"
        style={{ transform: 'translate(25%,25%)' }}
        dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }} />

      {/* ── Hero banner (full width) ─────────────────────── */}
      <div className="relative w-full px-5 sm:px-8 pt-6 pb-5 border-b border-white/[0.06]"
        style={{ background: 'linear-gradient(180deg, rgba(98,250,227,0.04) 0%, transparent 100%)' }}>
        <div className="flex items-start justify-between flex-wrap gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center border border-[#62FAE3]/20"
                style={{ background: 'rgba(98,250,227,0.08)' }}>
                <Network className="w-4 h-4 text-[#62FAE3]" />
              </div>
              <span className="text-[11px] font-bold tracking-widest text-[#62FAE3] uppercase">Creator Network</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-none">
              <span className="text-white">Discover </span>
              <span style={{ background: 'linear-gradient(106deg,#D0BCFF 0%,#62FAE3 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                Creators
              </span>
            </h1>
            <p className="text-[#8a94a6] text-sm mt-1.5">
              {allProfiles.length} creators · Ranked by real studio work
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/network/leaderboard')}
              className="border-white/10 bg-white/[0.04] text-amber-400 hover:text-amber-300 hover:bg-white/[0.08] hover:border-amber-500/30">
              <Trophy className="w-4 h-4 mr-1.5" /> Leaderboard
            </Button>
            <Button size="sm" onClick={() => navigate('/network/me')}
              className="font-bold text-[#080d14]"
              style={{ background: 'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)', boxShadow:'0 0 18px rgba(98,250,227,0.28)' }}>
              <Sparkles className="w-4 h-4 mr-1.5" />
              My Profile
            </Button>
          </div>
        </div>

        {/* Search + filter bar */}
        <div className="mt-4 flex gap-2 flex-wrap relative z-10">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a94a6]" />
            <input ref={searchRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Search creators…"
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder-[#8a94a6] focus:outline-none focus:border-[#62FAE3]/40 focus:bg-white/[0.08] transition-all" />
          </div>
          {/* Sort pills */}
          <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.07]">
            {SORTS.map(s => (
              <button key={s.id} type="button" onClick={() => setSortBy(s.id)}
                className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold transition-all ${sortBy === s.id ? 'text-[#080d14]' : 'text-[#8a94a6] hover:text-white'}`}
                style={sortBy === s.id ? { background:'linear-gradient(106deg,#D0BCFF,#62FAE3)' } : undefined}>
                <s.Icon className="w-3 h-3" />{s.label}
              </button>
            ))}
          </div>
          {/* Filter toggle */}
          <button type="button" onClick={() => setFiltersOpen(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-semibold transition-all ${filtersOpen || activeFilterCount > 0 ? 'border-[#62FAE3]/40 bg-[#62FAE3]/10 text-[#62FAE3]' : 'border-white/10 bg-white/[0.04] text-[#8a94a6] hover:text-white hover:bg-white/[0.07]'}`}>
            <Filter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && <span className="w-4 h-4 rounded-full bg-[#62FAE3] text-[#080d14] text-[10px] font-black flex items-center justify-center">{activeFilterCount}</span>}
          </button>
        </div>

        {/* Filter drawer */}
        <AnimatePresence>
          {filtersOpen && (
            <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }}
              className="overflow-hidden relative z-10 mt-3">
              <div className="pt-3 pb-1 border-t border-white/[0.07] flex flex-wrap gap-5">
                {/* Role */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-[#8a94a6] uppercase tracking-wider">Role</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ROLES.map(r => (
                      <button key={r.id} type="button" onClick={() => setRoleFilter(r.id === roleFilter ? '' : r.id)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${roleFilter === r.id ? 'border-[#D0BCFF]/50 bg-[#D0BCFF]/15 text-[#D0BCFF]' : 'border-white/[0.08] text-[#8a94a6] hover:border-white/20 hover:text-white'}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Level */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-[#8a94a6] uppercase tracking-wider">Level</p>
                  <div className="flex flex-wrap gap-1.5">
                    {LEVELS.map(l => (
                      <button key={l.id} type="button" onClick={() => setLevelFilter(l.id === levelFilter ? '' : l.id)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${levelFilter === l.id ? 'border-[#62FAE3]/50 bg-[#62FAE3]/10 text-[#62FAE3]' : 'border-white/[0.08] text-[#8a94a6] hover:border-white/20 hover:text-white'}`}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Badge */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-[#8a94a6] uppercase tracking-wider">Has Badge</p>
                  <div className="flex flex-wrap gap-1.5">
                    {BADGES.map(b => (
                      <button key={b} type="button" onClick={() => setBadgeFilter(b === badgeFilter ? '' : b)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${badgeFilter === b ? 'border-amber-400/50 bg-amber-400/10 text-amber-300' : 'border-white/[0.08] text-[#8a94a6] hover:border-white/20 hover:text-white'}`}>
                        {BADGE_LABELS[b]}
                      </button>
                    ))}
                  </div>
                </div>
                {activeFilterCount > 0 && (
                  <div className="flex items-end">
                    <button type="button" onClick={clearFilters}
                      className="flex items-center gap-1 text-xs text-[#8a94a6] hover:text-[#ff6b6b] transition-colors">
                      <X className="w-3 h-3" /> Clear all
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Body: Grid + Sidebar ─────────────────────────── */}
      <div className="flex gap-0 relative z-10">
        {/* Creator grid */}
        <div className="flex-1 min-w-0 p-5 sm:p-8">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[#62FAE3]" />
            </div>
          ) : displayed.length === 0 ? (
            <div className="text-center py-24">
              <Users className="w-12 h-12 mx-auto mb-4 text-[#62FAE3]/25" />
              <p className="text-white font-bold text-lg">{q || activeFilterCount > 0 ? 'No matches' : 'No creators yet'}</p>
              <p className="text-[#8a94a6] text-sm mt-1">
                {q || activeFilterCount > 0 ? 'Try different filters.' : 'Be the first to publish your profile!'}
              </p>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters}
                  className="mt-4 text-sm text-[#62FAE3] hover:underline">Clear filters</button>
              )}
              {!q && activeFilterCount === 0 && (
                <Button className="mt-5 font-bold text-[#080d14]"
                  style={{ background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)' }}
                  onClick={() => navigate('/network/me')}>
                  Set up my profile
                </Button>
              )}
            </div>
          ) : (
            <>
              <p className="text-[11px] text-[#8a94a6] mb-4">
                Showing <span className="text-white font-semibold">{displayed.length}</span>
                {activeFilterCount > 0 || q ? ' filtered' : ''} creator{displayed.length !== 1 ? 's' : ''}
                {activeFilterCount > 0 && <button type="button" onClick={clearFilters} className="ml-2 text-[#62FAE3] hover:underline">clear filters</button>}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {displayed.map((p, i) => (
                  <motion.div key={p.id || p.handle} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.03 }}>
                    <CreatorCard profile={p} onClick={() => navigate(`/network/${p.handle}`)} />
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Right sidebar: Suggestions + Prefs ──────────── */}
        <div className="hidden lg:flex flex-col gap-4 w-72 xl:w-80 flex-shrink-0 p-5 border-l border-white/[0.06]">
          {/* Suggested creators */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
            <div className="px-4 py-3 border-b border-white/[0.07] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#62FAE3]" />
              <span className="text-sm font-bold text-white">Suggested for You</span>
            </div>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[#62FAE3]" />
              </div>
            ) : suggestions.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-[#8a94a6] text-xs">Publish your profile to get suggestions!</p>
              </div>
            ) : (
              <div className="p-2">
                {suggestions.map(p => (
                  <SuggestCard key={p.id || p.handle} profile={p} onClick={() => navigate(`/network/${p.handle}`)} />
                ))}
              </div>
            )}
          </div>

          {/* Network stats */}
          {!loading && allProfiles.length > 0 && (
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
              <p className="text-sm font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#D0BCFF]" />
                Network Stats
              </p>
              {[
                { label: 'Total Creators', val: allProfiles.length },
                { label: 'Studio Leads', val: allProfiles.filter(p => p.progress?.level === 6).length },
                { label: 'Total XP Earned', val: allProfiles.reduce((s,p) => s + (p.progress?.xp||0), 0).toLocaleString() },
                { label: 'Avg XP / Creator', val: allProfiles.length ? Math.round(allProfiles.reduce((s,p) => s + (p.progress?.xp||0), 0) / allProfiles.length).toLocaleString() : '0' },
              ].map(({ label, val }) => (
                <div key={label} className="flex items-center justify-between text-xs">
                  <span className="text-[#8a94a6]">{label}</span>
                  <span className="font-bold text-white">{val}</span>
                </div>
              ))}
            </div>
          )}

          {/* Preferences */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#D0BCFF]" />
              Your Preferences
            </p>
            <p className="text-[11px] text-[#8a94a6] leading-relaxed">
              Personalize what you see in the directory. Publish your profile to enable full preferences.
            </p>
            <Button size="sm" className="w-full text-[#080d14] font-bold text-xs"
              style={{ background:'linear-gradient(106deg,#D0BCFF,#62FAE3)' }}
              onClick={() => navigate('/network/me')}>
              {myProfile ? 'Edit Profile & Prefs' : 'Set Up Profile'}
            </Button>
            {myProfile && (
              <div className="space-y-2 pt-1">
                {[
                  { label: 'Show my profile in directory', active: myProfile.published },
                  { label: 'Accept DM requests', active: myProfile.accepting_messages },
                ].map(({ label, active }) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#62FAE3]' : 'bg-white/10 border border-white/20'}`}>
                      {active && <Check className="w-2.5 h-2.5 text-[#080d14]" strokeWidth={3} />}
                    </div>
                    <span className="text-[11px] text-[#8a94a6]">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-1">
            <p className="text-xs font-bold text-[#8a94a6] uppercase tracking-wider mb-2">Quick Links</p>
            {[
              { label: 'Leaderboard', path: '/network/leaderboard', Icon: Trophy, col: '#f5c842' },
              { label: 'My Inbox', path: '/network/inbox', Icon: Users, col: '#D0BCFF' },
              { label: 'My Profile', path: '/network/me', Icon: Sparkles, col: '#62FAE3' },
            ].map(({ label, path, Icon, col }) => (
              <button key={path} type="button" onClick={() => navigate(path)}
                className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-white/[0.05] transition-all text-left">
                <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: col }} />
                <span className="text-xs text-[#c5cbd6] hover:text-white transition-colors">{label}</span>
                <ChevronRight className="w-3 h-3 text-[#8a94a6] ml-auto" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkDirectoryPage;
